import {
  Button,
  IconButton,
  InputAdornment,
  Link,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import Spinner from "../../components/reusableComponents/spinner/spinner";
import AuthLayout from "../../components/commonComponents/authLayout/authLayout";
import InlineAlert from "../../components/reusableComponents/inlineAlert/inlineAlert";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { LoginInfo } from "../../types/formTypes";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { loginUser } from "../../store/thunks/authenticationThunk";
import { persistor } from "../../store";
import { tokens } from "../../theme";
import {
  reset,
  setLoggedIn,
} from "../../store/features/authentication/authenticationSlice";

const loginInitialValues: LoginInfo = {
  username: "",
  password: "",
};

const validateLogin = Yup.object({
  username: Yup.string().email("Invalid email").required("Email is Required"),
  password: Yup.string()
    .min(8, "Too Short!")
    .max(50, "Too Long!")
    .required("Password is Required"),
});

// 48 on touch, 44 with a pointer — the touch-target floor without making the
// desktop form feel oversized.
const fieldSx = {
  "& .MuiOutlinedInput-root": { height: { xs: 48, md: 44 } },
};

const labelSx = {
  fontSize: 13,
  fontWeight: 500,
  color: tokens.color.body,
  mb: 0.75,
};

const Login = () => {
  const { loginLoading, error, previousLocation } = useAppSelector(
    (state: any) => state.authentication
  );

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState("");

  const loginForm = useFormik({
    initialValues: loginInitialValues,
    validationSchema: validateLogin,
    onSubmit: (values: LoginInfo) => {
      setLoginErrorMessage("");
      localStorage.clear();
      persistor.purge();

      dispatch(loginUser(values))
        .then((result) => {
          if (result?.payload?.result) {
            loginForm.resetForm();
            if (result?.payload?.two_factor) {
              navigate("/twoFactor");
            } else {
              dispatch(setLoggedIn(true));
              navigate("/dashboard");
            }
          } else {
            setLoginErrorMessage(result?.payload?.message);
          }
        })
        .catch(() => {});
    },
  });

  useEffect(() => {
    dispatch(reset());
    if (previousLocation?.includes("resetpasswordverification")) {
      setOpenSnackbar(true);
    }
  }, [dispatch, previousLocation]);

  // One panel, not the two stacked red strings this screen used to show.
  const failureMessage = loginErrorMessage || error?.data?.message;

  return (
    <AuthLayout brand>
      <Spinner loading={loginLoading} />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        open={openSnackbar}
        message="Password has been reset successfully"
        onClose={() => setOpenSnackbar(false)}
      />

      <form onSubmit={loginForm.handleSubmit} noValidate>
        <Typography variant="h2" sx={{ mb: 0.5 }}>
          Log in
        </Typography>
        <Typography sx={{ fontSize: tokens.typography.body, color: tokens.color.muted, mb: 3 }}>
          Use the email your account was created with.
        </Typography>

        <Typography component="label" htmlFor="username" sx={{ ...labelSx, display: "block" }}>
          Email
        </Typography>
        <TextField
          id="username"
          name="username"
          type="email"
          autoComplete="email"
          fullWidth
          sx={{ ...fieldSx, mb: 2.25 }}
          value={loginForm.values.username}
          error={loginForm.touched.username && Boolean(loginForm.errors.username)}
          helperText={loginForm.touched.username ? loginForm.errors.username : " "}
          onChange={loginForm.handleChange}
          onBlur={loginForm.handleBlur}
        />

        <Typography component="label" htmlFor="password" sx={{ ...labelSx, display: "block" }}>
          Password
        </Typography>
        <TextField
          id="password"
          name="password"
          type={showPassword ? "text" : "password"}
          autoComplete="current-password"
          fullWidth
          sx={{ ...fieldSx, mb: 1 }}
          value={loginForm.values.password}
          error={loginForm.touched.password && Boolean(loginForm.errors.password)}
          helperText={loginForm.touched.password ? loginForm.errors.password : " "}
          onChange={loginForm.handleChange}
          onBlur={loginForm.handleBlur}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((show) => !show)}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Link
          component="button"
          type="button"
          onClick={() => navigate("/resetpassword")}
          sx={{ fontSize: 13, fontWeight: 500, display: "block", mb: 2.5 }}
        >
          Forgot password?
        </Link>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loginLoading}
          sx={{ height: { xs: 48, md: 46 } }}
        >
          Log in
        </Button>

        {failureMessage && (
          <Typography component="div" sx={{ mt: 2.5 }}>
            <InlineAlert>{failureMessage}</InlineAlert>
          </Typography>
        )}
      </form>
    </AuthLayout>
  );
};

export default Login;
